import { Link } from "react-router-dom";
import { Instagram, Mail } from "lucide-react";
import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const LOGO = "/yourwishlist_logo.png";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);

    try {
      const r = await api.post("/newsletter", { email });
      toast.success(r.data.message || "Subscribed");
      setEmail("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Try again");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer
      data-testid="site-footer"
      className="bg-yw-ink text-white pt-24 pb-12 rounded-t-[32px] md:rounded-t-[64px] mt-32"
    >
      <div className="max-w-1440 mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-4">
          {/*
            CLS / IMAGE LAYOUT FIX:
            Keep the footer logo's exact rendered dimensions in both the HTML
            attributes and CSS. This reserves the image box before the asset
            finishes loading, preventing the footer/logo from shifting layout.
            The logo is below the main shopping content, so it is lazy-loaded
            to avoid competing with LCP resources.
          */}
          <img
            src={LOGO}
            alt="Your Wishlist"
            width="224"
            height="56"
            loading="lazy"
            decoding="async"
            className="block w-[224px] h-[56px] shrink-0 mb-6 object-contain invert brightness-0 opacity-95"
          />

          <p className="font-inter text-sm text-white/60 leading-relaxed max-w-xs mb-6">
            Crafted for every moment. Your Wishlist — earrings to make every day
            feel a little more yours.
          </p>

          <div className="flex items-center gap-4 text-white/60">
            <a
              href="https://www.instagram.com/your_wishlist_store_/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex w-10 h-10 items-center justify-center rounded-full hover:text-yw-gold hover:bg-white/5 transition-colors"
              data-testid="footer-instagram"
              aria-label="Your Wishlist on Instagram"
            >
              <Instagram size={18} aria-hidden="true" />
            </a>
          </div>
        </div>

        <div className="md:col-span-2">
          <h2 className="font-playfair text-lg mb-5">Shop</h2>
          <ul className="space-y-3 text-sm text-white/60 font-inter">
            <li><Link to="/shop/studs" className="hover:text-yw-gold transition-colors">Studs</Link></li>
            <li><Link to="/shop/hoops" className="hover:text-yw-gold transition-colors">Hoops</Link></li>
            <li><Link to="/shop/drops" className="hover:text-yw-gold transition-colors">Drops</Link></li>
            <li><Link to="/shop/statement" className="hover:text-yw-gold transition-colors">Statement</Link></li>
            <li><Link to="/shop/bridal" className="hover:text-yw-gold transition-colors">Bridal</Link></li>
          </ul>
        </div>

        <div className="md:col-span-2">
          <h2 className="font-playfair text-lg mb-5">Company</h2>
          <ul className="space-y-3 text-sm text-white/60 font-inter">
            <li><Link to="/about" className="hover:text-yw-gold transition-colors">About</Link></li>
            <li><Link to="/blog" className="hover:text-yw-gold transition-colors">Journal</Link></li>
            <li><Link to="/contact" className="hover:text-yw-gold transition-colors">Contact</Link></li>
            <li><a href="/care-guide" className="hover:text-yw-gold transition-colors">Care Guide</a></li>
            <li><a href="/sustainability" className="hover:text-yw-gold transition-colors">Sustainability</a></li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <h2 className="font-playfair text-lg mb-3">Join Our Exclusive Circle</h2>
          <p className="font-inter text-sm text-white/60 mb-5">
            Early access to new collections, private sales and styling notes.
          </p>

          <form
            onSubmit={subscribe}
            className="flex items-center gap-2"
            data-testid="footer-newsletter-form"
          >
            <div className="relative flex-1">
              <Mail
                size={16}
                aria-hidden="true"
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40"
              />
              <label htmlFor="footer-newsletter-email" className="sr-only">
                Email address
              </label>
              <input
                id="footer-newsletter-email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                data-testid="footer-newsletter-input"
                className="w-full bg-white/5 border border-white/10 rounded-[14px] py-3 pl-11 pr-4 text-sm font-inter placeholder:text-white/40 focus:outline-none focus:border-yw-gold"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="footer-newsletter-submit"
              className="px-5 py-3 bg-yw-gold text-yw-ink rounded-[14px] text-xs tracking-[0.18em] uppercase font-medium hover:bg-yw-lightGold transition-colors disabled:opacity-60"
            >
              {loading ? "..." : "Join"}
            </button>
          </form>
        </div>
      </div>

      <div className="max-w-1440 mx-auto px-6 md:px-12 mt-20 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-white/40 font-inter">
        <p>© {new Date().getFullYear()} Your Wishlist. All rights reserved.</p>
        <p className="tracking-[0.15em] uppercase">Made with care in India</p>
      </div>
    </footer>
  );
}
